import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { TabPanel, TabView } from "primereact/tabview";

import { PageProps } from "../../../models/routing.model";
import UiButton from "../../../components/button/Button";
import UiCard from "../../../components/card/Card";
import VariableHighlightEditor, {
	VariableHighlightEditorHandle,
} from "../../../components/variable-editor/VariableHighlightEditor";
import {
	MessageTemplate,
	MessageTemplatePreviewResult,
	emptyMessageTemplate,
} from "../../../models/entities/message-template.model";
import { useMessageTemplates, useTemplateVariables } from "../../../hooks/useMessageTemplates";
import { useInstitutionsDetailed } from "../../../hooks/useInstitutionsDetailed";
import { EndpointsService } from "../../../services/endpoints/endpoints.service";

const LIST_ROUTE = "/settings/message-templates";

const isSameTemplate = (a: MessageTemplate, b: MessageTemplate): boolean =>
	JSON.stringify(a) === JSON.stringify(b);

const SettingsMessageTemplateEdit = (props: PageProps) => {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const params = useParams<{ templateId: string }>();
	const toast = useRef<Toast>(null);
	const editorRef = useRef<VariableHighlightEditorHandle>(null);

	// `templateId` is "new" for the create flow; otherwise the numeric ID.
	const isNewRoute = params.templateId === "new";
	const numericId = isNewRoute ? null : Number(params.templateId);

	const templates = useMessageTemplates();
	const variables = useTemplateVariables();
	const institutions = useInstitutionsDetailed();
	const coaches = useQuery({
		queryKey: ["providers-minimal"],

		queryFn: () => EndpointsService.dataRetriever.getProvidersMinimalInfo(),
	});

	const [draft, setDraft] = useState<MessageTemplate | null>(null);
	const [original, setOriginal] = useState<MessageTemplate | null>(null);
	const isNew = draft !== null && draft.id === null;
	const isDirty = useMemo(
		() => (draft && original ? !isSameTemplate(draft, original) : false),
		[draft, original]
	);

	const [variableSearch, setVariableSearch] = useState("");
	const [testParticipantId, setTestParticipantId] = useState("");
	const [preview, setPreview] = useState<MessageTemplatePreviewResult | null>(null);

	const [isSaving, setIsSaving] = useState(false);
	const [isTesting, setIsTesting] = useState(false);

	// Bootstrap the draft from the URL: either start blank ("new") or hydrate
	// from the cached templates list once it loads.
	useEffect(() => {
		if (draft !== null) return;
		if (isNewRoute) {
			const fresh = emptyMessageTemplate();
			setDraft(fresh);
			setOriginal({ ...fresh });
			return;
		}
		if (numericId === null || Number.isNaN(numericId)) return;
		if (!templates.data) return;
		const found = templates.data.find((template) => template.id === numericId);
		if (found) {
			setDraft({ ...found });
			setOriginal({ ...found });
		}
	}, [draft, isNewRoute, numericId, templates.data]);

	const knownVariableNames = useMemo(
		() => (variables.data ?? []).map((variable) => variable.name),
		[variables.data]
	);

	const languageOptions = useMemo(() => {
		const fromTemplates = (templates.data ?? [])
			.map((template) => template.languageCode)
			.filter((code): code is string => !!code);
		const set = new Set<string>(["ENGLISH", "SPANISH", ...fromTemplates]);
		return Array.from(set).map((code) => ({ label: code, value: code }));
	}, [templates.data]);

	const institutionOptions = useMemo(
		() =>
			(institutions.data ?? []).map((institution) => ({
				label: institution.institutionName,
				value: institution.institutionId,
			})),
		[institutions.data]
	);

	const coachOptions = useMemo(
		() =>
			(coaches.data ?? []).map((coach) => ({
				label: `${coach.firstName} ${coach.lastName} (#${coach.coachId})`,
				value: coach.coachId,
			})),
		[coaches.data]
	);

	const filteredVariables = useMemo(() => {
		const term = variableSearch.trim().toLowerCase();
		const list = variables.data ?? [];
		if (!term) return list;
		return list.filter((variable) => variable.name.toLowerCase().includes(term));
	}, [variables.data, variableSearch]);

	const updateDraft = (changes: Partial<MessageTemplate>) =>
		setDraft((current) => (current ? { ...current, ...changes } : current));

	const goBackToList = () => navigate(LIST_ROUTE);

	const confirmDiscardIfDirty = (proceed: () => void) => {
		if (!isDirty) {
			proceed();
			return;
		}
		confirmDialog({
			message: t("MESSAGE_TEMPLATES.CONFIRM.DISCARD_MESSAGE"),
			header: t("MESSAGE_TEMPLATES.CONFIRM.DISCARD_HEADER"),
			icon: "pi pi-exclamation-triangle",
			acceptLabel: t("MESSAGE_TEMPLATES.CONFIRM.DISCARD_ACCEPT"),
			rejectLabel: t("MESSAGE_TEMPLATES.CONFIRM.CANCEL"),
			accept: proceed,
		});
	};

	const onSave = async () => {
		if (!draft) return;
		if (!draft.name.trim()) {
			toast.current?.show({
				severity: "warn",
				summary: t("MESSAGE_TEMPLATES.TOAST.VALIDATION"),
				detail: t("MESSAGE_TEMPLATES.VALIDATION.NAME_REQUIRED"),
			});
			return;
		}
		if (!draft.content.trim()) {
			toast.current?.show({
				severity: "warn",
				summary: t("MESSAGE_TEMPLATES.TOAST.VALIDATION"),
				detail: t("MESSAGE_TEMPLATES.VALIDATION.CONTENT_REQUIRED"),
			});
			return;
		}
		if (!draft.languageCode || !draft.languageCode.trim()) {
			toast.current?.show({
				severity: "warn",
				summary: t("MESSAGE_TEMPLATES.TOAST.VALIDATION"),
				detail: t("MESSAGE_TEMPLATES.VALIDATION.LANGUAGE_REQUIRED"),
			});
			return;
		}

		setIsSaving(true);
		try {
			const saved = isNew
				? await EndpointsService.dataRetriever.createMessageTemplate({ body: draft })
				: await EndpointsService.dataRetriever.updateMessageTemplate({
						id: draft.id as number,
						body: draft,
					});
			await templates.invalidate();
			setDraft({ ...saved });
			setOriginal({ ...saved });
			toast.current?.show({
				severity: "success",
				summary: t("MESSAGE_TEMPLATES.TOAST.SAVED"),
			});
			// After creating a brand-new template, replace the URL with its real ID
			// so subsequent saves go through the update path.
			if (isNew && saved.id !== null && saved.id !== undefined) {
				navigate(`${LIST_ROUTE}/${saved.id}`, { replace: true });
			}
		} catch (error) {
			toast.current?.show({
				severity: "error",
				summary: t("MESSAGE_TEMPLATES.TOAST.SAVE_ERROR"),
				detail: (error as AxiosError)?.message,
			});
		} finally {
			setIsSaving(false);
		}
	};

	const onDuplicate = () => {
		if (!draft) return;
		// Reset URL to "new" so the next save takes the create path. The current
		// draft survives the navigation because the component remounts but the
		// useEffect bootstrap will see isNewRoute = true.
		const copy: MessageTemplate = { ...draft, id: null };
		setDraft(copy);
		setOriginal({ ...copy });
		setPreview(null);
		navigate(`${LIST_ROUTE}/new`, { replace: false });
		toast.current?.show({
			severity: "info",
			summary: t("MESSAGE_TEMPLATES.TOAST.DUPLICATED"),
			detail: t("MESSAGE_TEMPLATES.TOAST.DUPLICATED_DETAIL"),
		});
	};

	const onTest = async () => {
		if (!draft) return;
		const participantId = Number(testParticipantId.trim());
		if (!testParticipantId.trim() || Number.isNaN(participantId)) {
			toast.current?.show({
				severity: "warn",
				summary: t("MESSAGE_TEMPLATES.TOAST.VALIDATION"),
				detail: t("MESSAGE_TEMPLATES.VALIDATION.PARTICIPANT_REQUIRED"),
			});
			return;
		}

		setIsTesting(true);
		setPreview(null);
		try {
			const result = await EndpointsService.dataRetriever.previewMessageTemplate({
				body: {
					participantId,
					content: draft.content,
					subject: draft.subject,
				},
			});
			setPreview(result);
		} catch (error) {
			const status = (error as AxiosError)?.response?.status;
			toast.current?.show({
				severity: "error",
				summary: t("MESSAGE_TEMPLATES.TOAST.TEST_ERROR"),
				detail:
					status === 404
						? t("MESSAGE_TEMPLATES.VALIDATION.PARTICIPANT_NOT_FOUND")
						: (error as AxiosError)?.message,
			});
		} finally {
			setIsTesting(false);
		}
	};

	const renderVariablePalette = () => (
		<div className="mt-variables">
			<div className="mt-variables-header">
				<span className="mt-variables-title">{t("MESSAGE_TEMPLATES.VARIABLES.TITLE")}</span>
				<span className="mt-variables-legend">
					<span className="mt-legend-dot mt-legend-dot--known" />
					{t("MESSAGE_TEMPLATES.VARIABLES.LEGEND_KNOWN")}
					<span className="mt-legend-dot mt-legend-dot--unknown" />
					{t("MESSAGE_TEMPLATES.VARIABLES.LEGEND_UNKNOWN")}
				</span>
			</div>
			<p className="mt-variables-help">{t("MESSAGE_TEMPLATES.VARIABLES.HELP")}</p>
			<span className="p-input-icon-left mt-variables-search">
				<i className="pi pi-search" />
				<InputText
					value={variableSearch}
					onChange={(event) => setVariableSearch(event.target.value)}
					placeholder={t("MESSAGE_TEMPLATES.VARIABLES.SEARCH_PLACEHOLDER")}
				/>
			</span>
			<div className="mt-variables-chips">
				{variables.isLoading && <span>{t("MESSAGE_TEMPLATES.VARIABLES.LOADING")}</span>}
				{!variables.isLoading && filteredVariables.length === 0 && (
					<span className="mt-variables-empty">
						{t("MESSAGE_TEMPLATES.VARIABLES.EMPTY")}
					</span>
				)}
				{filteredVariables.map((variable) => (
					<button
						key={variable.name}
						type="button"
						className="mt-chip"
						title={variable.type}
						onClick={() => editorRef.current?.insertVariable(variable.name)}
					>
						<i className="pi pi-plus mt-chip-icon" />
						{`{{${variable.name}}}`}
					</button>
				))}
			</div>
		</div>
	);

	const renderEditorHeader = () => (
		<div className="mt-editor-header-actions">
			{isDirty && (
				<span className="mt-dirty-badge">{t("MESSAGE_TEMPLATES.EDITOR.UNSAVED")}</span>
			)}
			<UiButton
				type="button"
				className="p-button-rounded p-button-sm p-button-outlined"
				icon="pi pi-arrow-left"
				label="MESSAGE_TEMPLATES.ACTIONS.BACK"
				onClick={() => confirmDiscardIfDirty(goBackToList)}
			/>
		</div>
	);

	const renderTestControls = () => (
		<div className="mt-test-controls">
			<p className="mt-section-help">{t("MESSAGE_TEMPLATES.TEST.HELP")}</p>
			<div className="mt-test-row">
				<label
					className="mt-field-label"
					htmlFor="mt-participant"
				>
					{t("MESSAGE_TEMPLATES.TEST.PARTICIPANT_LABEL")}
				</label>
				<span className="p-input-icon-left mt-test-input">
					<i className="pi pi-user" />
					<InputText
						id="mt-participant"
						value={testParticipantId}
						keyfilter="pint"
						onChange={(event) => setTestParticipantId(event.target.value)}
						placeholder={t("MESSAGE_TEMPLATES.TEST.PARTICIPANT_PLACEHOLDER")}
						onKeyDown={(event) => event.key === "Enter" && onTest()}
					/>
				</span>
				<UiButton
					type="button"
					className="p-button-rounded p-button-sm"
					icon="pi pi-play"
					label="MESSAGE_TEMPLATES.TEST.RUN"
					onClick={onTest}
					loading={isTesting}
					disabled={!draft}
				/>
			</div>
		</div>
	);

	const renderTestResult = () =>
		preview ? (
			<div className="mt-preview">
				{preview.unresolvedVariables.length > 0 && (
					<div className="mt-preview-unresolved">
						<i className="pi pi-exclamation-triangle" />
						<span>{t("MESSAGE_TEMPLATES.TEST.UNRESOLVED")}</span>
						{preview.unresolvedVariables.map((name) => (
							<Tag
								key={name}
								className="mt-unresolved-tag"
								value={`{{${name}}}`}
								severity="warning"
							/>
						))}
					</div>
				)}
				{preview.subject !== null && preview.subject !== undefined && (
					<div className="mt-preview-subject">
						<span className="mt-field-label">
							{t("MESSAGE_TEMPLATES.FIELDS.SUBJECT")}
						</span>
						<div className="mt-preview-subject-value">{preview.subject}</div>
					</div>
				)}
				<span className="mt-field-label">{t("MESSAGE_TEMPLATES.TEST.RESULT")}</span>
				<iframe
					title="message-template-preview"
					className="mt-preview-frame"
					sandbox=""
					srcDoc={preview.content ?? ""}
				/>
			</div>
		) : null;

	// Loading / not-found guards: templates list still loading, or the route ID
	// points to a template that no longer exists.
	if (!draft) {
		const notFound =
			!isNewRoute &&
			templates.data !== undefined &&
			(numericId === null ||
				Number.isNaN(numericId) ||
				!templates.data.some((template) => template.id === numericId));

		return (
			<div
				className="mt-page"
				id={props.id}
			>
				<Toast ref={toast} />
				<ConfirmDialog />
				<UiCard>
					<div className="mt-empty-editor">
						<i
							className={
								notFound
									? "pi pi-exclamation-triangle mt-empty-icon"
									: "pi pi-spin pi-spinner mt-empty-icon"
							}
						/>
						<p>
							{notFound
								? t("MESSAGE_TEMPLATES.EDITOR.EMPTY")
								: t("MESSAGE_TEMPLATES.VARIABLES.LOADING")}
						</p>
						<UiButton
							type="button"
							className="p-button-rounded p-button-outlined"
							icon="pi pi-arrow-left"
							label="MESSAGE_TEMPLATES.ACTIONS.BACK"
							onClick={goBackToList}
						/>
					</div>
				</UiCard>
			</div>
		);
	}

	return (
		<div
			className="mt-page"
			id={props.id}
		>
			<Toast ref={toast} />
			<ConfirmDialog />
			<UiCard
				title={
					isNew
						? "MESSAGE_TEMPLATES.EDITOR.TITLE_NEW"
						: "MESSAGE_TEMPLATES.EDITOR.TITLE_EDIT"
				}
				customHeader={renderEditorHeader()}
			>
				<TabView className="mt-editor-tabs">
					<TabPanel header={t("MESSAGE_TEMPLATES.EDITOR.TAB_CONFIG")}>
						<div className="row mt-editor-layout">
							<div className="col-12 col-xl-8 mt-editor-main">
								<div className="row">
									<div className="col-12 col-md-6 mt-field">
										<label
											className="mt-field-label"
											htmlFor="mt-name"
										>
											{t("MESSAGE_TEMPLATES.FIELDS.NAME")}
										</label>
										<InputText
											id="mt-name"
											value={draft.name}
											onChange={(event) =>
												updateDraft({ name: event.target.value })
											}
										/>
									</div>
									<div className="col-12 col-md-6 mt-field">
										<label
											className="mt-field-label"
											htmlFor="mt-language"
										>
											{t("MESSAGE_TEMPLATES.FIELDS.LANGUAGE")}
										</label>
										<Dropdown
											id="mt-language"
											value={draft.languageCode}
											options={languageOptions}
											editable
											onChange={(event) =>
												updateDraft({ languageCode: event.value })
											}
											placeholder={t(
												"MESSAGE_TEMPLATES.FIELDS.LANGUAGE_PLACEHOLDER"
											)}
										/>
									</div>
									<div className="col-12 col-md-6 mt-field">
										<label
											className="mt-field-label"
											htmlFor="mt-coach"
										>
											{t("MESSAGE_TEMPLATES.FIELDS.COACH")}
										</label>
										<Dropdown
											id="mt-coach"
											value={draft.coachId}
											options={coachOptions}
											filter
											showClear
											onChange={(event) =>
												updateDraft({ coachId: event.value ?? null })
											}
											placeholder={t("MESSAGE_TEMPLATES.FIELDS.COACH_ALL")}
										/>
									</div>
									<div className="col-12 col-md-6 mt-field">
										<label
											className="mt-field-label"
											htmlFor="mt-institution"
										>
											{t("MESSAGE_TEMPLATES.FIELDS.INSTITUTION")}
										</label>
										<Dropdown
											id="mt-institution"
											value={draft.institutionId}
											options={institutionOptions}
											filter
											showClear
											onChange={(event) =>
												updateDraft({
													institutionId: event.value ?? null,
												})
											}
											placeholder={t(
												"MESSAGE_TEMPLATES.FIELDS.INSTITUTION_ALL"
											)}
										/>
									</div>
									<div className="col-12 col-md-6 mt-field">
										<label
											className="mt-field-label"
											htmlFor="mt-subject"
										>
											{t("MESSAGE_TEMPLATES.FIELDS.SUBJECT")}
										</label>
										<InputText
											id="mt-subject"
											value={draft.subject ?? ""}
											onChange={(event) =>
												updateDraft({
													subject: event.target.value || null,
												})
											}
											placeholder={t(
												"MESSAGE_TEMPLATES.FIELDS.SUBJECT_PLACEHOLDER"
											)}
										/>
									</div>
									<div className="col-12 col-md-6 mt-field">
										<label
											className="mt-field-label"
											htmlFor="mt-origin"
										>
											{t("MESSAGE_TEMPLATES.FIELDS.ORIGIN")}
										</label>
										<InputText
											id="mt-origin"
											value={draft.origin ?? ""}
											onChange={(event) =>
												updateDraft({
													origin: event.target.value || null,
												})
											}
											placeholder={t(
												"MESSAGE_TEMPLATES.FIELDS.ORIGIN_PLACEHOLDER"
											)}
										/>
									</div>
								</div>

								<div className="mt-content-section">
									<label
										className="mt-field-label"
										htmlFor="mt-content"
									>
										{t("MESSAGE_TEMPLATES.FIELDS.CONTENT")}
									</label>
									<VariableHighlightEditor
										id="mt-content"
										ref={editorRef}
										value={draft.content}
										onChange={(value) => updateDraft({ content: value })}
										knownVariables={knownVariableNames}
										placeholder={t(
											"MESSAGE_TEMPLATES.FIELDS.CONTENT_PLACEHOLDER"
										)}
										minHeight={260}
										ariaLabel={t("MESSAGE_TEMPLATES.FIELDS.CONTENT")}
									/>
								</div>

								<div className="mt-actions">
									<UiButton
										type="button"
										className="p-button-rounded"
										icon="pi pi-save"
										label="MESSAGE_TEMPLATES.ACTIONS.SAVE"
										onClick={onSave}
										loading={isSaving}
									/>
									{!isNew && (
										<UiButton
											type="button"
											className="p-button-rounded p-button-outlined"
											icon="pi pi-copy"
											label="MESSAGE_TEMPLATES.ACTIONS.DUPLICATE"
											onClick={onDuplicate}
										/>
									)}
								</div>
							</div>

							<div className="col-12 col-xl-4 mt-editor-side">
								{renderVariablePalette()}
							</div>
						</div>
					</TabPanel>

					<TabPanel header={t("MESSAGE_TEMPLATES.EDITOR.TAB_TESTING")}>
						{renderTestControls()}
						{renderTestResult()}
					</TabPanel>
				</TabView>
			</UiCard>
		</div>
	);
};

export default SettingsMessageTemplateEdit;
